import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const LEVELS = ['essential', 'standard', 'technical'];

export const DEFAULT_VIEW_CONFIG = {
  // Tab-level visibility
  'tab.overview': 'essential',
  'tab.fields': 'essential',
  'tab.carbon': 'essential',
  'tab.climate': 'standard',
  'tab.trends': 'essential',
  'tab.outcomes': 'standard',
  'tab.dataset-audits': 'technical',
  'tab.calibration': 'technical',
  'tab.data-quality': 'technical',

  // Overview sub-sections
  'overview.carbonBalance': 'essential',
  'overview.fieldPerformance': 'essential',
  'overview.annualProgress': 'essential',
  'overview.outcomeProjections': 'essential',
  'overview.digitalTwin': 'essential',

  // Carbon sub-sections
  'carbon.budgetWaterfall': 'standard',
  'carbon.perFieldPools': 'standard',

  // FieldDetails sub-sections
  'fieldDetails.scaleContext': 'standard',
  'fieldDetails.referenceBenchmark': 'standard',
  'fieldDetails.brittlenessContext': 'standard',
  'fieldDetails.weeklyAssessment': 'technical',
  'fieldDetails.methodology': 'technical',

  // Terrain viewer sub-sections
  'terrain.overlays': 'standard',
  'terrain.contours': 'technical',
  'terrain.sunControls': 'standard',

  // Dashboard sub-sections
  'dashboard.processBars': 'standard',

  // ProcessCurveChart sub-sections
  'chart.processLines': 'standard',

  // Map: question-led journeys. Off until RB serves surface.questions;
  // admins switch it on in Admin > Views (Technical = staff preview).
  'map.askTheMap': 'hidden',
};

export const VIEW_COMPONENTS = [
  { key: 'tab.overview', label: 'Overview Tab', group: 'Tabs', protected: true },
  { key: 'tab.fields', label: 'Parcels Tab', group: 'Tabs', protected: true },
  { key: 'tab.carbon', label: 'Carbon Tab', group: 'Tabs' },
  { key: 'tab.climate', label: 'Growing Conditions Tab', group: 'Tabs' },
  { key: 'tab.trends', label: 'Trends Tab', group: 'Tabs' },
  { key: 'tab.outcomes', label: 'Outcomes Tab', group: 'Tabs' },
  { key: 'tab.dataset-audits', label: 'Dataset Audits Tab', group: 'Tabs' },
  { key: 'tab.calibration', label: 'Calibration Tab', group: 'Tabs' },
  { key: 'tab.data-quality', label: 'Data Quality Tab', group: 'Tabs' },

  { key: 'overview.carbonBalance', label: 'Carbon Balance Summary', group: 'Overview' },
  { key: 'overview.fieldPerformance', label: 'Field Performance vs Reference', group: 'Overview' },
  { key: 'overview.annualProgress', label: 'Annual Progress', group: 'Overview' },
  { key: 'overview.outcomeProjections', label: 'Outcome Projections', group: 'Overview' },
  { key: 'overview.digitalTwin', label: 'Property Digital Twin', group: 'Overview' },

  { key: 'carbon.budgetWaterfall', label: 'Carbon Budget Waterfall', group: 'Carbon' },
  { key: 'carbon.perFieldPools', label: 'Per-Field Carbon Pools', group: 'Carbon' },

  { key: 'fieldDetails.scaleContext', label: 'Score Scale Context', group: 'Parcel Details' },
  { key: 'fieldDetails.referenceBenchmark', label: 'Reference Benchmark', group: 'Parcel Details' },
  { key: 'fieldDetails.brittlenessContext', label: 'Brittleness Context', group: 'Parcel Details' },
  { key: 'fieldDetails.weeklyAssessment', label: 'Weekly Assessment Table', group: 'Parcel Details' },
  { key: 'fieldDetails.methodology', label: 'Methodology & Limitations', group: 'Parcel Details' },

  { key: 'terrain.overlays', label: 'Terrain Overlays (TPI, Drainage)', group: 'Terrain Viewer' },
  { key: 'terrain.contours', label: 'Contour Lines', group: 'Terrain Viewer' },
  { key: 'terrain.sunControls', label: 'Sun Position Controls', group: 'Terrain Viewer' },

  { key: 'dashboard.processBars', label: 'Process Bars on Farm Cards', group: 'Dashboard' },

  { key: 'chart.processLines', label: 'Individual Process Lines', group: 'Charts' },

  { key: 'map.askTheMap', label: 'Ask the map (question-led walk-through)', group: 'Map' },
];

const PROTECTED_KEYS = new Set(
  VIEW_COMPONENTS.filter(c => c.protected).map(c => c.key)
);

const ViewComplexityContext = createContext({
  level: 'standard',
  setLevel: () => {},
  isEssential: false,
  isStandard: true,
  isTechnical: false,
  showIf: () => true,
  isVisible: () => true,
  viewConfig: {},
});

export function ViewComplexityProvider({ children }) {
  const [level, setLevelRaw] = useState('standard');
  const [viewConfig, setViewConfig] = useState({});

  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setLevelRaw('standard');
        return;
      }
      const unsubDoc = onSnapshot(doc(db, 'users', user.uid), (snap) => {
        if (snap.exists()) {
          const viewLevel = snap.data().viewLevel;
          if (LEVELS.includes(viewLevel)) {
            setLevelRaw(viewLevel);
          } else {
            setLevelRaw('standard');
          }
        }
      }, () => {
        setLevelRaw('standard');
      });
      return unsubDoc;
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'viewConfiguration'), (snap) => {
      if (snap.exists()) {
        setViewConfig(snap.data());
      } else {
        setViewConfig({});
      }
    }, () => {
      setViewConfig({});
    });
    return unsub;
  }, []);

  const setLevel = useCallback(() => {}, []);

  const mergedConfig = useMemo(() => ({ ...DEFAULT_VIEW_CONFIG, ...viewConfig }), [viewConfig]);

  const value = useMemo(() => ({
    level,
    setLevel,
    isEssential: level === 'essential',
    isStandard: level === 'standard',
    isTechnical: level === 'technical',
    showIf: (minLevel) => {
      const idx = LEVELS.indexOf(level);
      const minIdx = LEVELS.indexOf(minLevel);
      return idx >= minIdx;
    },
    isVisible: (componentKey) => {
      const minLevel = mergedConfig[componentKey] ?? 'essential';
      if (minLevel === 'hidden') return false;
      const idx = LEVELS.indexOf(level);
      const minIdx = LEVELS.indexOf(minLevel);
      return idx >= minIdx;
    },
    viewConfig: mergedConfig,
  }), [level, setLevel, mergedConfig]);

  return <ViewComplexityContext.Provider value={value}>{children}</ViewComplexityContext.Provider>;
}

export function useViewComplexity() {
  return useContext(ViewComplexityContext);
}

export { PROTECTED_KEYS };
export const COMPLEXITY_LEVELS = LEVELS;

export const VIEW_LEVEL_LABELS = {
  essential: 'Essential',
  standard: 'Standard',
  technical: 'Technical',
};
